import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { DivergentObject } from 'src/app/shared/utils/role-permission-divergence.util';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

/** Which post-save state the detail pane is showing (design-spec §7 states 7 and 8). */
export type SaveResultStatus = 'saved' | 'diverged';

/**
 * The outcome of the last save, read back from the server (design-spec §8). `saved` is a clean success;
 * `diverged` means the server rewrote part of the payload and `objects` names what it did not store.
 */
export interface RolePermissionSaveResult {
  status: SaveResultStatus;
  objects: DivergentObject[];
}

/**
 * Post-save result banner for the Roles-tab detail pane (DASH-3179, part 8/9; design-spec §7 states 7–8, §8).
 *
 * A dumb, `OnPush`, presentational banner driven entirely by the `result` input:
 * - **saved** (state 7) — a green `role="status"` banner stating the cache reality: the change applies on
 *   each holder's next request. Paired with the container's success toast.
 * - **diverged** (state 8) — an amber `role="status"` banner naming every object the server did not store as
 *   posted, with the reason (permissions unavailable to this role were stripped on save). The affected rows
 *   keep an amber edge in the matrix until this banner is dismissed. Object names come from the backend
 *   taxonomy — never a raw `GLOBAL_*` / `COID_*` id (design-spec §9, DS-13).
 *
 * Dismissible via {@link dismiss}; the container clears the result and the row highlight together.
 */
@Component({
  standalone: false,
  selector: 'app-role-permission-save-result-banner',
  templateUrl: './role-permission-save-result-banner.component.html',
  styleUrls: ['./role-permission-save-result-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolePermissionSaveResultBannerComponent {
  /** The last save outcome; `null` renders nothing. */
  @Input() result: RolePermissionSaveResult | null = null;

  /** Requests the banner be cleared (and the matrix's divergence edge with it). */
  @Output() dismiss = new EventEmitter<void>();

  /** `DivergentObject` has no stable `id` (keyed by `objectId`), so track by index for the divergence list. */
  trackByIndex = trackByIndex;
}
